import api from './api';

class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  /**
   * Register service worker
   */
  async registerServiceWorker() {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    try {
      this.registration = await navigator.serviceWorker.register('/custom-service-worker.js');
      console.log('Service Worker registered:', this.registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      return this.registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);

    return permission;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    try {
      // Ensure service worker is registered
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      // Request permission if needed
      if (Notification.permission === 'default') {
        const permission = await this.requestPermission();
        if (permission !== 'granted') {
          throw new Error('Notification permission denied');
        }
      }

      if (Notification.permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      // Get VAPID public key from server
      console.log('Fetching VAPID public key from server...');
      const { publicKey } = await api.getVapidPublicKey();
      console.log('Received VAPID public key:', publicKey);

      // Convert base64 VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(publicKey);
      console.log('Converted application server key, length:', applicationServerKey.length);

      // Subscribe to push
      console.log('Subscribing to push manager...');
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      console.log('Push subscription created:', this.subscription);

      // Send subscription to server
      console.log('Sending subscription to server...');
      await api.subscribeToPushNotifications(this.subscription);
      console.log('Subscription successfully saved to server');

      return this.subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);

      // Provide more helpful error messages
      if (error.name === 'AbortError' || error.message.includes('push service error')) {
        throw new Error('Unable to connect to push service. This can happen on some local networks or VPNs. Try disabling VPN or firewall temporarily.');
      }

      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      if (!this.subscription) {
        // Try to get existing subscription
        const registration = await navigator.serviceWorker.ready;
        this.subscription = await registration.pushManager.getSubscription();
      }

      if (!this.subscription) {
        console.log('No active push subscription');
        return;
      }

      // Unsubscribe from browser
      await this.subscription.unsubscribe();

      // Remove from server
      await api.unsubscribeFromPushNotifications(this.subscription.endpoint);

      this.subscription = null;
      console.log('Push subscription removed');
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      throw error;
    }
  }

  /**
   * Check if user is currently subscribed
   */
  async isSubscribed() {
    try {
      if (!this.isSupported()) return false;

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      this.subscription = subscription;
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Utility: Convert base64 string to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

const pushNotificationManager = new PushNotificationManager();
export default pushNotificationManager;
